import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, tap } from 'rxjs/operators';
import { CompanyHolidayViewModel } from 'src/app/settings/models/company-holiday-view-model';
import { ConsoleCloudSettings } from 'src/app/settings/models/console-cloud.model';
import { DisclaimerAndTerms } from 'src/app/settings/models/disclaimer-and-terms.model';
import { EmailTemplateModel } from 'src/app/settings/models/email-template.model';
import { EnabledIntegrations } from 'src/app/settings/models/enabled-integrations.model';
import { LeadOrgAdmin } from 'src/app/settings/models/lead-org-admin.model';
import { NotificationSettingsView } from 'src/app/settings/models/notification-settings-view.model';
import { PalaceSettings } from 'src/app/settings/models/palace.model';
import { PaymentSettings } from 'src/app/settings/models/payment-settings.model';
import { PropertyManagerSettings } from 'src/app/settings/models/property-manager-settings.model';
import { PropertyMeResponseModel } from 'src/app/settings/models/property-me-response-model';
import { PropertyMeSettings } from 'src/app/settings/models/property-me.model';
import { PropertyTreeSettings } from 'src/app/settings/models/property-tree.model';
import { ReapitSettings } from 'src/app/settings/models/reapit.model';
import { WorkCalendar } from 'src/app/settings/models/work-calendar.model';
import { Events } from 'src/app/shared/constants/events.constant';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';
import { CacheService } from '../cache.service';
import { EventService } from '../event.service';
import { SmsPackageDetails } from 'src/app/settings/models/sms-package-details.model';

@Injectable({
  providedIn: 'root',
})
export class SettingsManagementService extends BaseService {
  private readonly urlPrefix = `${environment.agencyMgmt.urlPrefix}/SettingsManagement`;

  constructor(
    http: HttpClient,
    private eventService: EventService,
    private cacheService: CacheService
  ) {
    super(http);
  }

  public communicationPreferences() {
    return this.get<NotificationSettingsView>(
      `${this.urlPrefix}/notifications-preferences`
    );
  }

  public updateCommunicationPreferences(
    notificationSettingsViewModel: NotificationSettingsView
  ) {
    return this.patch<NotificationSettingsView>(
      `${this.urlPrefix}/update-notification-preferences`,
      notificationSettingsViewModel
    );
  }

  public workCalendar() {
    return this.get<WorkCalendar>(`${this.urlPrefix}/work-calendar`);
  }

  public updateWorkCalendar(workCalendarBaseViewmodel: WorkCalendar) {
    return this.patch<WorkCalendar>(
      `${this.urlPrefix}/update-work-calendar`,
      workCalendarBaseViewmodel
    );
  }

  public holidayShedule() {
    return this.get<CompanyHolidayViewModel>(
      `${this.urlPrefix}/holiday-shedule`
    );
  }

  public updateHolidayShedule(
    companyHolidayBaseViewmodel: CompanyHolidayViewModel
  ) {
    return this.patch<CompanyHolidayViewModel>(
      `${this.urlPrefix}/update-holiday-shedule`,
      companyHolidayBaseViewmodel
    );
  }

  public disclaimerAndTerms() {
    return this.get<DisclaimerAndTerms>(
      `${this.urlPrefix}/disclaimer-and-terms`
    );
  }

  updateDisclaimer(disclaimerNote: DisclaimerAndTerms) {
    return this.patch<String>(
      `${this.urlPrefix}/update-disclaimer`,
      disclaimerNote
    );
  }
  updateDisclaimerForAfterBusinessHours(
    disclaimerForAfterBusinessHoursNote: DisclaimerAndTerms
  ) {
    return this.patch<String>(
      `${this.urlPrefix}/update-disclaimer-for-after-business-hours`,
      disclaimerForAfterBusinessHoursNote
    );
  }
  updateTermsAndConditions(termsAndConditionNotes: DisclaimerAndTerms) {
    return this.patch<String>(
      `${this.urlPrefix}/update-terms-and-conditions`,
      termsAndConditionNotes
    );
  }

  updateDisclaimerForEmergencyJobs(termsAndConditionNotes: DisclaimerAndTerms) {
    return this.patch<String>(
      `${this.urlPrefix}/update-disclaimer-for-emergency-jobs`,
      termsAndConditionNotes
    );
  }

  public emailTemplate() {
    return this.get<EmailTemplateModel>(`${this.urlPrefix}/email-template`);
  }

  public UpdateEmailTemplate(emailTemplateViewModel: EmailTemplateModel) {
    return this.patch<EmailTemplateModel>(
      `${this.urlPrefix}/update-email-template`,
      emailTemplateViewModel
    );
  }

  public getEnabledIntegrations() {
    return this.get<EnabledIntegrations>(
      `${this.urlPrefix}/org-enabled-integrations`
    );
  }

  public integrate(enabledIntegrations: EnabledIntegrations) {
    return this.patch<EnabledIntegrations>(
      `${this.urlPrefix}/integrate`,
      enabledIntegrations
    );
  }

  public getPropertyTreeSettings() {
    return this.get<PropertyTreeSettings>(
      `${this.urlPrefix}/property-tree-settings`
    );
  }

  public updatePropertyTreeSettings(
    propertyTreeSettings: PropertyTreeSettings
  ) {
    return this.patch<PropertyTreeSettings>(
      `${this.urlPrefix}/update-property-tree-settings`,
      propertyTreeSettings
    );
  }

  public getReapitSettings() {
    return this.get<ReapitSettings>(
      `${this.urlPrefix}/reapit-settings`
    );
  }

  public updateReapitSettings(
    reapitSettings: ReapitSettings
  ) {
    return this.patch<ReapitSettings>(
      `${this.urlPrefix}/update-reapit-settings`,
      reapitSettings
    );
  }

  public getPropertyMeSettings() {
    return this.get<PropertyMeSettings>(
      `${this.urlPrefix}/property-me-settings`
    );
  }

  public updatePropertyMeSettings(propertyMeSettings: PropertyMeSettings) {
    return this.patch<PropertyMeResponseModel>(
      `${this.urlPrefix}/update-property-me-settings`,
      propertyMeSettings
    );
  }

  public getPalaceSettings() {
    return this.get<PalaceSettings>(`${this.urlPrefix}/palace-settings`);
  }

  public updatePalaceSettings(palaceSettings: PalaceSettings) {
    return this.patch<PalaceSettings>(
      `${this.urlPrefix}/update-palace-settings`,
      palaceSettings
    );
  }

  public getPaymentSettings() {
    return this.cacheService
      .get(
        `${this.constructor.name}_${this.getPaymentSettings.name}`,
        `no-key`,
        this.get<PaymentSettings[]>(`${this.urlPrefix}/payment-settings`),
        [Events.PaymentDetailChangedEvent]
      )
      .pipe(filter((resp) => resp != null));
  }

  public addCard(data: any) {
    return this.post<any>(`${this.urlPrefix}/add-card`, data).pipe(
      tap(() => {
        this.eventService.dispatch(Events.PaymentDetailChangedEvent);
      })
    );
  }

  public setAsDefaultCard(data: any) {
    return this.post<any>(`${this.urlPrefix}/default-card`, data);
  }

  public removeCard(data: any) {
    return this.post<any>(`${this.urlPrefix}/remove-card`, data).pipe(
      tap(() => {
        this.eventService.dispatch(Events.PaymentDetailChangedEvent);
      })
    );
  }

  public setDefaultAddOn(data: any) {
    return this.post<any>(`${this.urlPrefix}/default-addOn`, data);
  }

  public getConsoleCouldSettings() {
    return this.get<ConsoleCloudSettings>(
      `${this.urlPrefix}/console-cloud-settings`
    );
  }

  public updateConsoleCouldSettings(
    propertyTreeSettings: ConsoleCloudSettings
  ) {
    return this.patch<ConsoleCloudSettings>(
      `${this.urlPrefix}/update-console-cloud-settings`,
      propertyTreeSettings
    );
  }

  public propertyManagerSettings() {
    return this.get<PropertyManagerSettings>(
      `${this.urlPrefix}/property-manager`
    );
  }

  public updatePropertyManagerSettings(
    propertyManagerSettings: PropertyManagerSettings
  ) {
    return this.patch<PropertyManagerSettings>(
      `${this.urlPrefix}/property-manager`,
      propertyManagerSettings
    ).pipe(
      tap(() => {
        this.eventService.dispatch(Events.OrganizationSettingsChangedEvent);
      })
    );
  }

  public getLeadOrgAdminSettings() {
    return this.get<LeadOrgAdmin[]>(`${this.urlPrefix}/lead-org-admin-settings`);
  }

  public updateLeadOrgAdminSettings(leadOrgAdmins) {
    return this.patch<any>(`${this.urlPrefix}/lead-org-admin-settings`, leadOrgAdmins);
  }

  public getSmsPackageDetails(){
    return this.get<SmsPackageDetails>(`${this.urlPrefix}/sms-packages`);
  }

  public addSmsPackages(data: any){
    return this.post<any>(`${this.urlPrefix}/add-sms-packages`, data);
  }
}
